/* KRAVOS — global Add to Cart -> Cart Drawer enforcement
 * Every product add form on the storefront adds via Ajax and opens the
 * existing cart drawer. The customer is never sent to the dedicated /cart page
 * from an Add to Cart action.
 */
(() => {
  'use strict';

  const rootPath = (window.Shopify?.routes?.root) || '/';
  const addUrl = (window.routes?.cart_add_url) || (rootPath + 'cart/add');

  const isCartAddForm = (form) => {
    if (!form || form.tagName !== 'FORM') return false;
    const action = form.getAttribute('action') || form.action || '';
    try {
      const url = new URL(action, window.location.origin);
      const addPath = new URL(addUrl, window.location.origin).pathname.replace(/\/$/, '');
      return url.pathname.replace(/\/$/, '') === addPath || /\/cart\/add$/.test(url.pathname.replace(/\/$/, ''));
    } catch (_) {
      return /\/cart\/add(?:\.js)?(?:$|\?)/.test(action);
    }
  };

  const setLoading = (button, loading) => {
    if (!button) return;
    if (loading) {
      button.dataset.kravosAtcBusy = 'true';
      button.dataset.kravosAtcLabel = button.querySelector('span')?.textContent || button.textContent.trim();
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading', 'is-loading');
      button.disabled = true;
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('loading', 'is-loading');
      button.disabled = false;
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  };

  const setButtonText = (button, text) => {
    if (!button) return;
    const span = button.querySelector('span:not(.loading__spinner)');
    if (span) span.textContent = text;
    else button.textContent = text;
  };

  async function openFreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) throw new Error('KRAVOS cart drawer is not available');

    if (typeof window.KravosRefreshCartDrawer === 'function') {
      await window.KravosRefreshCartDrawer({ open: true });
      return;
    }

    // Fallback for the short window before kravos-cart-premium.js is ready.
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    throw new Error('KRAVOS cart drawer API is not ready');
  }

  async function addFormToDrawer(form, button) {
    const formData = new FormData(form);
    if (!formData.get('id')) throw new Error('No product variant selected');

    setLoading(button, true);
    // Open the drawer BEFORE the network request. The customer sees the cart
    // immediately; the live cart contents hydrate in the background.
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); } catch (_) {}
    }
    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add product to cart');
      }

      // Refresh asynchronously after the drawer is already visible.
      if (typeof window.KravosRefreshCartDrawer === 'function') {
        window.KravosRefreshCartDrawer({ open: false }).catch((error) => console.error('[KRAVOS] Drawer hydration:', error));
      }
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'kravos-global-add-to-cart', cartData: data }
      }));

      button.classList.add('kravos-atc-added');
      setButtonText(button, '✓ ADDED');
      window.setTimeout(() => {
        if (!button.isConnected) return;
        button.classList.remove('kravos-atc-added');
        setButtonText(button, button.dataset.kravosAtcLabel || 'BUY NOW');
        setLoading(button, false);
      }, 1200);
    } catch (error) {
      console.error('[KRAVOS] Add to cart failed:', error);
      setButtonText(button, 'TRY AGAIN');
      setLoading(button, false);
      window.setTimeout(() => {
        if (button.isConnected) setButtonText(button, button.dataset.kravosAtcLabel || 'BUY NOW');
      }, 1800);
    }
  }

  // Capture before Dawn/product-form.js can submit normally. This makes the
  // behavior consistent across product cards, collection grids, featured
  // products, product pages and dynamically injected product forms.
  document.addEventListener('submit', (event) => {
    const form = event.target?.closest?.('form');
    if (!isCartAddForm(form)) return;

    const submitter = event.submitter || form.querySelector('[name="add"], button[type="submit"], input[type="submit"]');
    if (submitter?.dataset.kravosAtcBusy === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addFormToDrawer(form, submitter).catch((error) => {
      console.error('[KRAVOS] Global Add to Cart handler:', error);
      setLoading(submitter, false);
    });
  }, true);

  // Some theme/app blocks use a direct /cart/add link instead of a form.
  // Convert those Add to Cart links to the same drawer behavior when a variant
  // id is available in the URL.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) return;
    if (anchor.dataset.kravosCartAddHandled === 'true') return;

    let url;
    try { url = new URL(anchor.href, window.location.href); } catch (_) { return; }
    const path = url.pathname.replace(/\/$/, '');
    if (!/\/cart\/add$/.test(path)) return;

    const variantId = url.searchParams.get('id') || url.searchParams.get('variant');
    if (!variantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    anchor.dataset.kravosCartAddHandled = 'true';

    const body = new URLSearchParams();
    body.set('id', variantId);
    body.set('quantity', url.searchParams.get('quantity') || '1');

    const fakeForm = document.createElement('form');
    fakeForm.action = addUrl;
    const id = document.createElement('input');
    id.name = 'id';
    id.value = variantId;
    fakeForm.appendChild(id);
    const quantity = document.createElement('input');
    quantity.name = 'quantity';
    quantity.value = url.searchParams.get('quantity') || '1';
    fakeForm.appendChild(quantity);

    addFormToDrawer(fakeForm, anchor).finally(() => {
      anchor.dataset.kravosCartAddHandled = 'false';
    });
  }, true);
})();
